import icon from "@fyendalscollection/marketing/assets/icon.svg";
import styles from "@fyendalscollection/marketing/pages/PageHome.module.scss";
import feature1d from "@fyendalscollection/marketing/assets/1d.png";
import feature1l from "@fyendalscollection/marketing/assets/1l.png";
import feature2d from "@fyendalscollection/marketing/assets/2d.png";
import feature2l from "@fyendalscollection/marketing/assets/2l.png";
import feature3d from "@fyendalscollection/marketing/assets/3d.png";
import feature3l from "@fyendalscollection/marketing/assets/3l.png";
import feature4d from "@fyendalscollection/marketing/assets/4d.png";
import feature4l from "@fyendalscollection/marketing/assets/4l.png";
import { Button, ButtonStyle } from "@fyendalscollection/marketing/lib/components/Button";
import { CardCanvas } from "@fyendalscollection/marketing/CardCanvas";
import { APP_BASE_URL } from "@fyendalscollection/marketing/lib/config";
import React, { useCallback, useMemo, useRef } from "react";
import { ThemedImage } from "@fyendalscollection/marketing/lib/components/ThemedImage";
import { useQueryParams } from "@fyendalscollection/marketing/lib/useQueryParams";
import { DiscordInvite } from "@fyendalscollection/marketing/lib/components/DiscordInvite";

export const PageHome = (): JSX.Element => {
	const queryParams = useQueryParams();
	const attr = queryParams.get("attr");
	const appUrl = useMemo(() => attr ? `${APP_BASE_URL}?attr=${attr}` : APP_BASE_URL, [attr]);

	const featuresRef = useRef<HTMLDivElement>(null);

	const onGetStarted = useCallback(() => window.location.href = appUrl, [appUrl]);
	const onLearnMore = useCallback(() => {
		if (!featuresRef.current) {
			return;
		}

		featuresRef.current.scrollIntoView({ behavior: "smooth" });
	}, []);

	return (
		<>
			<CardCanvas />

			<div className={styles.banner}>
				<div></div>
				<div>
					<div></div>
					<div>
						<div className={styles.content}>
							<div className={styles.title}>
								<img src={icon} />
								<div>Fyendal&apos;s Collection</div>
							</div>
							<div className={styles.subtitle}>
								The dedicated Flesh and Blood financial toolkit for collectors
							</div>
							<div className={styles.buttons}>
								<Button onClick={onGetStarted}>Get Started</Button>
								<Button onClick={onLearnMore} style={ButtonStyle.Secondary}>Learn More</Button>
							</div>
						</div>
					</div>
					<div></div>
				</div>
				<div></div>
			</div>

			<div className={styles.login}>
				Already have an account?<br /><a href={appUrl}>Login</a>
			</div>

			<div ref={featuresRef}>
				<Feature title="Industry-leading Transactional Collection Tracking" content={<ThemedImage srcDark={feature1d} srcLight={feature1l} />}>
					Fyendal&apos;s Collection uses a transactional model to track
					your purchases and sales, giving you a clear view of your
					collection&apos;s statistics over time.

					<ul>
						<li>Currency-aware trading and pricing</li>
						<li>Calculate gains across multiple market price sources</li>
						<li>Graph all collection statistics over time</li>
						<li>Set goals for your collection and measure your progress and costs to complete</li>
						<li>Import and Export via CSV</li>
					</ul>
				</Feature>

				<Feature title="Real-time EV Calculations" content={<ThemedImage srcDark={feature3d} srcLight={feature3l} />}>
					Check the estimated value of your sealed product at any time.

					<ul>
						<li>View changes in EV for sealed product over time</li>
						<li>All pull rates and calculations are accessible and public</li>
					</ul>
				</Feature>

				<Feature title="Market Trends" content={<ThemedImage srcDark={feature2d} srcLight={feature2l} />}>
					Deep insights in to purchase and sale trends in the Flesh and
					Blood market.

					<ul>
						<li>Biggest gains and losses over time.</li>
						<li>Full sale volume information for all products</li>
					</ul>
				</Feature>

				<Feature title="...and a lot more!" content={<ThemedImage srcDark={feature4d} srcLight={feature4l} />}>
					There&apos;s a lot to enjoy with Fyendal&apos;s Collection!

					<ul>
						<li>Test your luck by rolling for various profile emblems</li>
						<li>View pricing information for tournament decks recorded by LSS</li>
						<li>Record complex trades or box openings with bulk tooling</li>
					</ul>
				</Feature>
			</div>

			<DiscordInvite />
		</>
	);
};

interface FeatureProps {
	title: string;
	content: React.ReactNode;
	children: React.ReactNode;
}

const Feature = ({ title, content, children }: FeatureProps): JSX.Element => {
	return (
		<div className={styles.feature}>
			<div>
				<div>{title}</div>
				<div>{children}</div>
			</div>
			{content}
		</div>
	);
};
